/* Graph of Life brand v1.0.0 · tokens.json */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import InteractiveBackground from '@/components/ui/InteractiveBackground';
import SectionHeading from '@/components/gol/SectionHeading';
import CaveatCallout from '@/components/gol/CaveatCallout';
import { contact } from '@/data/siteConfig';
import { track } from '@/components/gol/ReviewCta';

const FIELDS = [
  { id: 'name', label: 'Name', type: 'input', placeholder: 'Full name' },
  { id: 'organization', label: 'Organization', type: 'input', placeholder: 'Institution or company' },
  { id: 'email', label: 'Professional email', type: 'input', inputType: 'email', placeholder: 'name@organization.com' },
  {
    id: 'decision',
    label: 'Decision or problem to investigate',
    type: 'textarea',
    placeholder: 'The decision that needs support, and who is accountable for it.',
  },
  {
    id: 'scope',
    label: 'Asset, geography, project or portfolio scope',
    type: 'input',
    placeholder: 'One asset, basin, project or bounded portfolio slice.',
  },
  {
    id: 'evidence',
    label: 'Current evidence or data situation',
    type: 'textarea',
    placeholder: 'What evidence exists today, and what is known to be missing.',
  },
  { id: 'timeline', label: 'Desired timeline', type: 'input', placeholder: 'Within the next quarter, for example.' },
];

const emptyForm = FIELDS.reduce((acc, f) => ({ ...acc, [f.id]: '' }), {});

const inputClass =
  'w-full rounded-sm border border-abyss-700 bg-abyss-950 px-4 py-3 text-base text-sand-50 placeholder:text-sand-50/35 focus:border-teal-400 focus:outline-none transition-colors duration-200';

const Scope = () => {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const onChange = (id) => (e) => setForm((prev) => ({ ...prev, [id]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const fields = (Array.isArray(body?.detail) ? body.detail : [])
          .map((d) => FIELDS.find((f) => f.id === d.loc?.[1])?.label)
          .filter(Boolean);
        const serviceMessage = typeof body?.detail === 'string' && body.detail.trim().length <= 240
          ? body.detail.trim()
          : '';
        throw new Error(
          fields.length
            ? `Please give a little more detail for ${[...new Set(fields)].join(', ')}.`
            : `${serviceMessage ? `${serviceMessage} ` : ''}Please email your inquiry while the form is unavailable.`
        );
      }
      track('inquiry_submitted', { kind: 'dependency_exposure_review' });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  return (
    <div data-testid="scope-page" className="bg-abyss-950 text-sand-50 relative overflow-x-hidden">
      <InteractiveBackground viewMode="FINANCIAL" density={0.7} />

      <section className="relative px-5 md:px-8 pt-32 md:pt-40 pb-24">
        <div className="max-w-6xl mx-auto grid gap-14 lg:grid-cols-[1fr_1.1fr] items-start">
          <div className="space-y-8">
            <SectionHeading as="h1" eyebrow="Dependency Exposure Review" heading="Start with the decision.">
              <p>
                Tell me what you need to investigate and the evidence you hold today.
                One asset, basin, project or portfolio slice is enough to begin a scoping conversation.
              </p>
            </SectionHeading>

            <CaveatCallout label="Boundary" testId="scope-boundary">
              The review supports evidence-led investigation and decision preparation. Validated credit,
              underwriting, pricing and prudential outputs belong to separate approval processes.
            </CaveatCallout>

            <p className="text-sm leading-relaxed text-sand-50/70">
              Your details are used to assess and respond to this inquiry. Share a general outline and keep
              confidential documents for a secure follow-up.
            </p>

            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-sand-50/35">
              Prefer email?{' '}
              <a
                href={`mailto:${contact.email}?subject=${encodeURIComponent(contact.reviewSubject)}`}
                data-testid="scope-email-fallback"
                className="text-teal-400 hover:text-sand-50 transition-colors duration-200"
              >
                {contact.email}
              </a>
            </p>
          </div>

          {status === 'sent' ? (
            <div
              data-testid="scope-success"
              className="gol-panel gol-panel-lead p-8 md:p-10 space-y-5"
            >
              <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-living-bright">
                <Check size={14} aria-hidden="true" />
                Inquiry recorded
              </span>
              <h2 className="font-serif text-2xl md:text-3xl font-light leading-snug">Thank you. Your inquiry is recorded.</h2>
              <p className="text-sm leading-relaxed text-sand-50/70">
                The next step is to establish scope and whether a review fits your question.
                For anything time-sensitive, reach me at {contact.email}.
              </p>
              <Link
                to="/systems"
                data-testid="scope-success-proof-link"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-teal-400 hover:text-sand-50 transition-colors duration-200"
              >
                Inspect the proof library
                <ArrowUpRight size={14} aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="scope-form"
              noValidate
              className="gol-panel gol-panel-live p-7 md:p-9 space-y-6"
            >
              {FIELDS.map((field) => (
                <div key={field.id} className="space-y-2">
                  <label
                    htmlFor={field.id}
                    className="block font-mono text-[10px] uppercase tracking-[0.2em] text-sand-50/35"
                  >
                    {field.label}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      id={field.id}
                      name={field.id}
                      required
                      rows={3}
                      value={form[field.id]}
                      onChange={onChange(field.id)}
                      placeholder={field.placeholder}
                      data-testid={`scope-field-${field.id}`}
                      className={inputClass}
                    />
                  ) : (
                    <input
                      id={field.id}
                      name={field.id}
                      required
                      type={field.inputType || 'text'}
                      value={form[field.id]}
                      onChange={onChange(field.id)}
                      placeholder={field.placeholder}
                      data-testid={`scope-field-${field.id}`}
                      className={inputClass}
                    />
                  )}
                </div>
              ))}

              {status === 'error' && (
                <p data-testid="scope-error" role="alert" className="text-sm text-risk-bright">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                data-testid="scope-submit"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-sm bg-teal-400 text-abyss-950 font-mono text-xs uppercase tracking-[0.16em] hover:bg-teal-500 transition-colors duration-200 disabled:opacity-60"
              >
                {status === 'sending' ? 'Sending' : 'Send the inquiry'}
                <ArrowUpRight size={14} aria-hidden="true" />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Scope;
